/**
 * `/login` (§17.1 admin auth, D-003): email → magic link (or dev code when the API surfaces DEV_LOGIN_ENABLED via
 * `method: 'DEV_CODE'`). Passkeys are offered as the phishing-resistant path for privileged roles.
 */
import { Button, Field, Input } from '@windrop/ui';
import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { useSession } from '../../auth/session';
import { identityApi } from '../../lib/api/admin';
import { isApiError } from '../../lib/api/client';
import { AuthLayout } from './AuthLayout';

export interface LoginFlowState {
  challengeId: string;
  method: 'MAGIC_LINK' | 'DEV_CODE';
  email: string;
  expiresAt: string;
  /** Verified first factor, carried to the TOTP / passkey step. */
  code?: string;
  next?: string;
}

export const useNextPath = (): string => {
  const params = new URLSearchParams(useLocation().search);
  const next = params.get('next');
  return next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
};

export function LoginPage() {
  const { status } = useSession();
  const navigate = useNavigate();
  const next = useNextPath();
  const [email, setEmail] = useState('');
  const [tenantSlug, setTenantSlug] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (status === 'authenticated') return <Navigate to={next} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setError('Enter the work email address your organizer invited.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await identityApi.login({ email, tenantSlug: tenantSlug || undefined });
      const state: LoginFlowState = {
        challengeId: res.challengeId,
        method: res.method,
        email,
        expiresAt: res.expiresAt,
        next,
      };
      navigate('/login/verify', { state });
    } catch (err) {
      setError(
        isApiError(err) ? err.message : 'Could not start sign-in. Check your connection and try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Sign in">
      <form onSubmit={submit} className="wd-stack" noValidate>
        <Field label="Work email" required hint="We send a one-time sign-in link; no password.">
          <Input
            preset="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
            data-testid="login-email"
          />
        </Field>
        <Field label="Tenant" hint="Only needed when your email belongs to more than one organizer.">
          <Input
            value={tenantSlug}
            onChange={(e) => setTenantSlug(e.target.value)}
            autoComplete="organization"
          />
        </Field>
        {error ? (
          <p role="alert" className="text-error">
            {error}
          </p>
        ) : null}
        <Button type="submit" loading={busy} loadingLabel="Sending link" block>
          Send sign-in link
        </Button>
        <p className="wd-muted">
          Have a passkey?{' '}
          <Link
            to={`/login/passkey${next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`}
            state={{ email }}
          >
            Sign in with a passkey
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
